import React, {
  useState,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { useSelector, useDispatch } from "react-redux";
import { injectIntl } from 'react-intl';
import A from 'components/A';
import { FILTERS_COMPONENT_TYPES_CATALOG, FILTER_TYPES } from 'hooks/useScreenFilters/constants';
import FiltersPanel from 'components/FiltersPanel';
import SearchFilter from 'components/SearchFilter';
import AplanetIcon from 'components/AplanetIcon';
import useOrganizations from 'utils/useOrganizations';
import { requestOrganizationDetail } from 'actions/api';

import './style.less';

const REQUEST_PERIODICITY_OPTIONS = ['month', 'quarter', 'semester', 'year'];
const ATTACHMENT_OPTIONS = ['doc','pdf','xls','image','other'];
const ATTACHMENT_ICON_OPTIONS = {
  doc: 'File-word',
  pdf: 'File-pdf',
  xls: 'File number',
  image: 'File image',
  other: 'File text empty'
};


const KpiDetailFilesFilters = ({
  intl,
  setFilterState,
  filterState,
  resetAllFilters,
  areFiltersSet,
  isTextFilterSet,
}) => {
  const dispatch = useDispatch();

  // Search
  const [hasToClearKpiSearch, setHasToClearKpiSearch] = useState(false);

  // UPLOADED
  const [userFilterValues, setUserFilterValues] = useState(filterState.user_filter);
  const [uploadDateFilterValues, setUploadDateFilterValues] = useState(filterState.upload_date_filter);

  // PERIODS
  const [periodDateFilterValues, setPeriodDateFilterValues] = useState(filterState.period_date_filter);
  const [periodicityFilterValues, setPeriodicityFilterValues] = useState(filterState.periodicity_filter);

  // ATTACHMENT
  const [attachmentFilterValues, setAttachmentFilterValues] = useState(filterState.attachment_filter);

  
  // Data
  const {
    organization,
    suborganization,
  } = useOrganizations();
 
  const {
    data: profile
  } = useSelector(state => state.profile);


  const {
    data: organizationDetail,
  } = useSelector((state) => state.organization_detail);

const members = useMemo(()=>{
  let users = organizationDetail?.users || [], external_users = organizationDetail?.external_users || [];
  const patchedProfile = [{
    member_slug : profile.slug,
    member_name: profile.name,
    member_email: profile.email,
    member_status: profile.status
  }];
  users = [...users,...patchedProfile, ...external_users];

  return users
  .filter(({member_status}) => member_status !== 'deleted')
  .map(user => {
    return {
      slug: user.member_slug ,
      name: `${user.member_name} (${user.member_email})`,
    }  
  })
  .sort((a,b) => {
    const A = a.name.toUpperCase();
    const B = b.name.toUpperCase();
    if (A < B) {
      return -1;
    }
    if (A > B) {
      return 1;
    }
    return 0;
  });

},[organizationDetail, profile]);

  useEffect(() => {
      dispatch(
        requestOrganizationDetail(organization.slug, suborganization.slug)
      )
  }, [organization, suborganization, dispatch]);

  const filtersConfig = useMemo(()=>(
    [
      {
        columnTitle: intl.formatMessage({id: `kpidetail_files_filter_column_title_upload`}),
        filters: [
          {
            filterType: FILTERS_COMPONENT_TYPES_CATALOG.multiSelect,
            filterTitle: intl.formatMessage({id: `kpidetail_files_filter_title_upload_by`}),
            title: intl.formatMessage({id: `kpidetail_files_filter_title_upload_search`}),
            selected: userFilterValues,
            onSelect: setUserFilterValues,
            options : members
          },
          {
            filterType: FILTERS_COMPONENT_TYPES_CATALOG.date,
            filterTitle: intl.formatMessage({id: `kpidetail_files_filter_title_upload_date`}),
            picker: 'date',
            format: 'YYYY-MM-DD',
            suffixIcon: (<AplanetIcon name="Calendar" />),
            value: uploadDateFilterValues,
            onChange: setUploadDateFilterValues,
          },
        ]
      },
      {
        columnTitle: intl.formatMessage({id: `kpidetail_files_filter_column_title_period`}),
        filters: [
          {
            filterType: FILTERS_COMPONENT_TYPES_CATALOG.date,
            filterTitle: intl.formatMessage({id: `kpidetail_files_filter_title_dates`}),
            picker: 'date',
            format: 'YYYY-MM-DD',
            suffixIcon: (<AplanetIcon name="Calendar" />),
            value: periodDateFilterValues,
            onChange: setPeriodDateFilterValues,
          },
          {
            filterType: FILTERS_COMPONENT_TYPES_CATALOG.list,
            filterTitle: intl.formatMessage({id: `kpidetail_files_filter_title_periodicity`}),
            listOptions: REQUEST_PERIODICITY_OPTIONS.map(option => ({
              value: option,
              title: intl.formatMessage({id: `kpidetail_files_filter_periodicity_${option}`}),
              label: (<span className={`Files__periodicity ${option}`}>{intl.formatMessage({id: `kpidetail_files_filter_periodicity_${option}`})}</span>)
            })),
            setSelectedOptions: setPeriodicityFilterValues,
            selectedOptions: periodicityFilterValues,
          },
        ]
      },
      {
      columnTitle: intl.formatMessage({id: `kpidetail_files_filter_column_title_attachment`}),
      filters: [
        {
          filterType: FILTERS_COMPONENT_TYPES_CATALOG.checkbox,
          filterTitle: intl.formatMessage({id: `kpidetail_files_filter_title_attachment`}),
          listOptions: ATTACHMENT_OPTIONS.map(option => ({
            value: option,
            title: intl.formatMessage({id: `attachment_${option}_type`}),
            label: (
            <>
              <AplanetIcon className="FilterList__value__attachment__logo" name={ATTACHMENT_ICON_OPTIONS[option]} />
            <span className='FilterList__value__attachment'>{intl.formatMessage({id: `attachment_${option}_type`})}</span>
            </>
            )
          })),
          setSelectedOptions: setAttachmentFilterValues,
          selectedOptions: attachmentFilterValues,
        },
      ]
    },

    ]),[
      intl,
      userFilterValues,
      uploadDateFilterValues,
      periodDateFilterValues,
      periodicityFilterValues,
      attachmentFilterValues,
      members
    ]);

  const onSetFilters = useCallback(() => {
    setFilterState(FILTER_TYPES.periodicity_filter)(periodicityFilterValues);
    setFilterState(FILTER_TYPES.attachment_filter)(attachmentFilterValues);
    setFilterState(FILTER_TYPES.user_filter)(userFilterValues);
    setFilterState(FILTER_TYPES.period_date_filter)(periodDateFilterValues);
    setFilterState(FILTER_TYPES.upload_date_filter)(uploadDateFilterValues);
  },[
    periodicityFilterValues,
    attachmentFilterValues,
    uploadDateFilterValues,
    userFilterValues,
    periodDateFilterValues,
    setFilterState
  ]);

  const onCancelFilter = useCallback(() => {
    setPeriodicityFilterValues(filterState.periodicity_filter);
    setAttachmentFilterValues(filterState.attachment_filter);
    setUploadDateFilterValues(filterState.upload_date_filter);
    setUserFilterValues(filterState.user_filter);
    setPeriodDateFilterValues(filterState.period_date_filter);

  },[
    filterState.periodicity_filter,
    filterState.attachment_filter,
    filterState.user_filter,
    filterState.period_date_filter,
    filterState.upload_date_filter
  ]);



  const onClearSearchAndFilters = useCallback(() => {
    setPeriodicityFilterValues([]);
    setAttachmentFilterValues([]);
    setUserFilterValues([]);
    setPeriodDateFilterValues([]);
    setUploadDateFilterValues([]);
    setHasToClearKpiSearch(true);
    resetAllFilters();
  }, [
    resetAllFilters,
  ]);

  return (
    <div className="KpiDetailFiles__container">
      <div className="KpiDetailFiles__table-filters">
        <FiltersPanel
          areFiltersSet={areFiltersSet}
          onCancelFilter={onCancelFilter}
          onSetFilters={onSetFilters}
          filtersConfig={filtersConfig}
          columnSpan={{ xs: 24, sm: 12, md: 8, lg: 8 }}
        />
        <SearchFilter
          textFilter={filterState.text_filter}
          onSetSearch={(searchText) => setFilterState(FILTER_TYPES.text_filter)(searchText)}
          hasToClearSearch={hasToClearKpiSearch}
          setHasToClearKpiSearch={setHasToClearKpiSearch}
          defaultOpen={isTextFilterSet}
          placeholder={intl.formatMessage({id: `datamanagement_files_filter_search__placeholder`})}
          autoFocus
        />

        <div className='KpiDetailFiles__remove-filters-wrapper'>
          <A
            className='KpiDetailFiles__remove-filters'
            onClick={onClearSearchAndFilters}
            disabled={!areFiltersSet && !isTextFilterSet}
          >
            {intl.formatMessage({id: 'datamanagement_files_filter_delete_all'})}
          </A>
        </div>
      </div>
    </div>
  );
}

export default injectIntl(KpiDetailFilesFilters);
